import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PRODUCTS } from '../../data/products';
import { INDUSTRIES } from '../../data/industries';
import { trackEvent } from '../../lib/analytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');
const QUOTE_EMAIL = 'info@leiongroup.com';

const buildQuoteMailto = (data) => {
  const subject = encodeURIComponent('Quote request from Leion Engineering website');
  const body = encodeURIComponent(
    [
      `Name: ${data.name}`,
      `Company: ${data.company || 'Not provided'}`,
      `Email: ${data.email}`,
      `Phone: ${data.phone}`,
      `Industry: ${data.industry || 'Not selected'}`,
      `Product: ${data.product || 'Not selected'}`,
      `Capacity: ${data.capacity || 'Not provided'}`,
      '',
      'Requirements:',
      data.message || 'Not provided',
    ].join('\n')
  );

  return `mailto:${QUOTE_EMAIL}?subject=${subject}&body=${body}`;
};

export const QuoteForm = ({ preselectedProduct = null, compact = false }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    industry: '',
    product: preselectedProduct || '',
    capacity: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    trackEvent('quote_submit', {
      label: compact ? 'Compact quote form' : 'Full quote form',
      metadata: {
        product: formData.product,
        industry: formData.industry,
        compact,
      },
    });

    try {
      await axios.post(`${BACKEND_URL}/api/quote-request`, formData);
      trackEvent('quote_success', {
        label: compact ? 'Compact quote form submitted' : 'Full quote form submitted',
        metadata: {
          product: formData.product,
          industry: formData.industry,
          compact,
        },
      });
      toast.success('Quote request submitted successfully! We will contact you shortly.');
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        industry: '',
        product: preselectedProduct || '',
        capacity: '',
        message: '',
      });
    } catch (error) {
      trackEvent('quote_fallback', {
        label: compact ? 'Compact quote form email fallback' : 'Full quote form email fallback',
        metadata: {
          product: formData.product,
          industry: formData.industry,
          compact,
        },
      });
      window.location.href = buildQuoteMailto(formData);
      toast.info('Could not reach the quote server, so we opened a direct email instead.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (compact) {
    const compactFieldClass = 'rounded-sm border-slate-300 bg-slate-50 text-slate-950 caret-slate-950 placeholder:text-slate-500 focus:ring-2 focus:ring-[#1E40AF]';

    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form-compact">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="compact-name" className="sr-only">Your Name</Label>
            <Input
              id="compact-name"
              placeholder="Your Name *"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              className={compactFieldClass}
              data-testid="quote-name"
            />
          </div>
          <div>
            <Label htmlFor="compact-company" className="sr-only">Company</Label>
            <Input
              id="compact-company"
              placeholder="Company"
              value={formData.company}
              onChange={(e) => handleChange('company', e.target.value)}
              className={compactFieldClass}
              data-testid="quote-company"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="compact-email" className="sr-only">Email</Label>
            <Input
              id="compact-email"
              type="email"
              placeholder="Email *"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              className={compactFieldClass}
              data-testid="quote-email"
            />
          </div>
          <div>
            <Label htmlFor="compact-phone" className="sr-only">Phone</Label>
            <Input
              id="compact-phone"
              type="tel"
              placeholder="Phone *"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              required
              className={compactFieldClass}
              data-testid="quote-phone"
            />
          </div>
        </div>
        <Label htmlFor="compact-message" className="sr-only">Your requirements</Label>
        <Textarea
          id="compact-message"
          placeholder="Your requirements..."
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
          rows={3}
          className={compactFieldClass}
          data-testid="quote-message"
        />
        <Button
          type="submit"
          disabled={loading}
          className="tech-shimmer w-full rounded-sm bg-[#1E40AF] py-4 font-bold uppercase tracking-wider text-white shadow-[0_20px_50px_rgba(30,64,175,0.35)] hover:bg-[#2563EB]"
          data-testid="quote-submit-compact"
        >
          {loading ? 'Submitting...' : 'Request Quote'}
        </Button>
      </form>
    );
  }

  const labelClass = 'text-sm font-semibold uppercase tracking-wider text-white';
  const fieldClass = 'rounded-sm border-slate-300 bg-slate-50 text-slate-950 caret-slate-950 placeholder:text-slate-500 focus:ring-2 focus:ring-[#1E40AF]';
  const selectContentClass = 'border-slate-300 bg-white text-slate-950';
  const selectItemClass = 'text-slate-950 focus:bg-[#DBEAFE] focus:text-slate-950';

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form-full">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name" className={labelClass}>Full Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            className={fieldClass}
            data-testid="quote-name-full"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company" className={labelClass}>Company Name</Label>
          <Input
            id="company"
            value={formData.company}
            onChange={(e) => handleChange('company', e.target.value)}
            className={fieldClass}
            data-testid="quote-company-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="email" className={labelClass}>Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            required
            className={fieldClass}
            data-testid="quote-email-full"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className={labelClass}>Phone Number *</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            required
            className={fieldClass}
            data-testid="quote-phone-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label className={labelClass}>Industry</Label>
          <Select value={formData.industry} onValueChange={(value) => handleChange('industry', value)}>
            <SelectTrigger className={fieldClass} data-testid="quote-industry">
              <SelectValue placeholder="Select Industry" />
            </SelectTrigger>
            <SelectContent className={selectContentClass}>
              {INDUSTRIES.map((industry) => (
                <SelectItem key={industry.id} value={industry.id} className={selectItemClass}>
                  {industry.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className={labelClass}>Product Interest</Label>
          <Select value={formData.product} onValueChange={(value) => handleChange('product', value)}>
            <SelectTrigger className={fieldClass} data-testid="quote-product">
              <SelectValue placeholder="Select Product" />
            </SelectTrigger>
            <SelectContent className={selectContentClass}>
              {PRODUCTS.map((product) => (
                <SelectItem key={product.id} value={product.id} className={selectItemClass}>
                  {product.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="capacity" className={labelClass}>Required Capacity</Label>
        <Input
          id="capacity"
          placeholder="e.g., 60 Tonnes, 3000 kg, etc."
          value={formData.capacity}
          onChange={(e) => handleChange('capacity', e.target.value)}
          className={fieldClass}
          data-testid="quote-capacity"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className={labelClass}>Additional Requirements</Label>
        <Textarea
          id="message"
          placeholder="Tell us about your specific requirements, site conditions, timeline, etc."
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
          rows={4}
          className={fieldClass}
          data-testid="quote-message-full"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="tech-shimmer w-full rounded-sm bg-[#1E40AF] px-12 py-5 font-bold uppercase tracking-wider text-white shadow-[0_20px_50px_rgba(30,64,175,0.35)] transition-all hover:bg-[#2563EB] md:w-auto"
        data-testid="quote-submit-full"
      >
        {loading ? 'Submitting Request...' : 'Submit Quote Request'}
      </Button>
    </form>
  );
};

export default QuoteForm;
